import { AddOutlined } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import { Classification, Position } from '@synergy/models';
import { Filter, PoolSpinner, SearchFilter } from '@synergy/components';
import { FormattedMessage } from 'react-intl';
import { Navigate, Route, Routes, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { PoolDocumentTitle } from '@synergy/components-next';
import { useEffect, useRef, useState } from 'react';

import { ClassificationDetailModal } from '../classifications/classifications-detail-modal';
import { ClassificationsOverview } from '../classifications/classifications-overview';
import { ConfigurationTab } from '../models/configuration-tab';
import { PaloActionMenu } from '../../../../common/components/palo/palo-action-menu';
import { PaloConfigurationShell } from '../../palo/palo-configuration-shell';
import { PaloLanguageSwitch } from '../../../../common/components/palo/palo-language-switch';
import { PositionDetailModal } from '../positions/postion-detail-modal';
import { PositionsOverview } from '../positions/positions-overview';
import { TopicDetail, TopicDetailHandle } from '../topics/topic-detail';
import { TopicsOverview } from '../topics/topics-overview';
import { defaultSearchParamsSelector } from '../../../../store/default-searchparams-selector';
import { resetSelectedLanguage } from '../../../../common/actions/selected-language-actions';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

const TABS = [
    { value: ConfigurationTab.Topics, label: 'app.configuration.topics' },
    { value: ConfigurationTab.Classifications, label: 'app.configuration.classifications' },
    { value: ConfigurationTab.Positions, label: 'app.configuration.positions' },
];

export const PaloConfigurationOrganizationOverview = () => {
    const dispatch = useThunkDispatch();
    const { requesting: positionsRequesting } = useAppSelector(state => state.positions);
    const { requesting: classificationsRequesting } = useAppSelector(state => state.classifications);
    const defaultSearchParams = useAppSelector(defaultSearchParamsSelector);
    const [searchParams, setSearchParams] = useSearchParams(defaultSearchParams);
    const navigate = useNavigate();
    const location = useLocation();

    const [isPositionModalOpen, setIsPositionModalOpen] = useState(false);
    const [selectedPosition, setSelectedPosition] = useState<Position | undefined>(undefined);
    const [isClassificationModalOpen, setIsClassificationModalOpen] = useState(false);
    const [selectedClassification, setSelectedClassification] = useState<Classification | undefined>(undefined);
    const topicDetailRef = useRef<TopicDetailHandle>(null);

    useEffect(() => {
        dispatch(resetSelectedLanguage());
    }, [dispatch]);

    const handlePositionsEdit = (position: Position) => {
        setSelectedPosition(position);
        setIsPositionModalOpen(true);
    };

    const handleClassificationsEdit = (classification: Classification) => {
        setSelectedClassification(classification);
        setIsClassificationModalOpen(true);
    };

    const handleNewPosition = () => {
        setSelectedPosition(undefined);
        setIsPositionModalOpen(true);
    };

    const handleNewClassification = () => {
        setSelectedClassification(undefined);
        setIsClassificationModalOpen(true);
    };

    const handleNewTopic = () => {
        dispatch(resetSelectedLanguage());
        navigate(ConfigurationTab.Topics + '/new');
    };

    const handlePositionModalClose = () => {
        setSelectedPosition(undefined);
        setIsPositionModalOpen(false);
    };

    const handleClassificationModalClose = () => {
        setSelectedClassification(undefined);
        setIsClassificationModalOpen(false);
    };

    const handleTopicDetailSave = () => {
        topicDetailRef.current?.submitForm();
    };

    // The topic detail owns the screen; its save action replaces the list tools.
    const isTopicDetail = /\/topics\/[^/]+$/.test(location.pathname);
    const activeTab = TABS.find(tab => location.pathname.includes(tab.value))?.value ?? ConfigurationTab.Topics;

    return (
        <PoolSpinner
            enabled={classificationsRequesting || positionsRequesting}
            sx={{ display: 'block', height: '100%' }}
        >
            <PoolDocumentTitle titleTranslationId={'app.configuration.title.organisation'} />
            <Filter searchParams={searchParams} onUpdateSearchParams={setSearchParams}>
                <PaloConfigurationShell
                    title="app.configuration.title.organisation"
                    tabs={TABS}
                    hideTabs={isTopicDetail}
                    actions={
                        isTopicDetail ? (
                            <Button variant="contained" color="primary" size="small" onClick={handleTopicDetailSave}>
                                <FormattedMessage id="app.common.save" />
                            </Button>
                        ) : (
                            <>
                                <Box sx={{ width: 220 }}>
                                    <SearchFilter />
                                </Box>
                                <PaloLanguageSwitch />
                                <PaloActionMenu
                                    primary={true}
                                    label={<FormattedMessage id="app.dashboard.new" />}
                                    icon={<AddOutlined />}
                                    items={[
                                        {
                                            label: <FormattedMessage id="app.configuration.newTopic" />,
                                            onClick: handleNewTopic,
                                        },
                                        {
                                            label: <FormattedMessage id="app.configuration.newClassification" />,
                                            onClick: handleNewClassification,
                                        },
                                        {
                                            label: <FormattedMessage id="app.configuration.newPosition" />,
                                            onClick: handleNewPosition,
                                        },
                                    ]}
                                />
                            </>
                        )
                    }
                >
                    <Routes>
                        <Route
                            index={true}
                            element={
                                <Navigate
                                    to={{ pathname: ConfigurationTab.Topics, search: searchParams.toString() }}
                                    replace={true}
                                />
                            }
                        />
                        <Route path={ConfigurationTab.Topics + '/*'}>
                            <Route index={true} element={<TopicsOverview />} />
                            <Route path={':id'} element={<TopicDetail ref={topicDetailRef} />} />
                        </Route>
                        <Route
                            path={ConfigurationTab.Classifications}
                            element={<ClassificationsOverview onEdit={handleClassificationsEdit} />}
                        />
                        <Route
                            path={ConfigurationTab.Positions}
                            element={<PositionsOverview onEdit={handlePositionsEdit} />}
                        />
                    </Routes>
                </PaloConfigurationShell>
            </Filter>
        </PoolSpinner>
    );
};
